/**
 * Referral Code Helper Utilities
 *
 * Provides safe, validated handling of referral codes stored in localStorage.
 * Note: Tenant referral codes are permanent and never expire, but localStorage
 * storage has an expiration for cleanup purposes.
 *
 * Implements security best practices to prevent XSS and data injection attacks.
 */

export const REFERRAL_CODE_KEY = 'referralCode';
export const REFERRAL_CODE_EXPIRES_AT_KEY = 'referralCodeExpiresAt';
export const REFERRAL_CODE_MIN_LENGTH = 4;
export const REFERRAL_CODE_MAX_LENGTH = 50;
export const REFERRAL_CODE_EXPIRY_DAYS = 30;

/**
 * Validates a referral code format
 * Only allows alphanumeric characters, hyphens, and underscores
 *
 * @param code - The referral code to validate
 * @returns true if the code is valid, false otherwise
 */
export const isValidReferralCode = (code: string): boolean => {
  if (!code || typeof code !== 'string') {
    return false;
  }

  // Remove any characters that aren't alphanumeric, hyphen, or underscore
  const sanitized = code.replace(/[^a-zA-Z0-9-_]/g, '');

  // Check if anything was removed (indicates invalid characters)
  if (sanitized !== code) {
    return false;
  }

  // Check length constraints
  return (
    sanitized.length >= REFERRAL_CODE_MIN_LENGTH &&
    sanitized.length <= REFERRAL_CODE_MAX_LENGTH
  );
};

/**
 * Sanitizes a referral code by removing invalid characters
 *
 * @param code - The referral code to sanitize
 * @returns The sanitized code, or null if invalid
 */
export const sanitizeReferralCode = (code: string): string | null => {
  if (!code || typeof code !== 'string') {
    return null;
  }

  const sanitized = code.replace(/[^a-zA-Z0-9-_]/g, '');

  if (
    sanitized.length < REFERRAL_CODE_MIN_LENGTH ||
    sanitized.length > REFERRAL_CODE_MAX_LENGTH
  ) {
    return null;
  }

  return sanitized;
};

/**
 * Stores a referral code in localStorage with an expiration timestamp
 * Note: This is for temporary storage during the signup process.
 * Tenant referral codes themselves are permanent.
 *
 * @param code - The referral code to store
 * @param expiryDays - Number of days until expiration (default: 90)
 * @returns true if stored successfully, false otherwise
 */
export const storeReferralCode = (
  code: string,
  expiryDays: number = REFERRAL_CODE_EXPIRY_DAYS
): boolean => {
  const sanitized = sanitizeReferralCode(code);

  if (!sanitized) {
    console.warn('Invalid referral code format:');
    return false;
  }

  try {
    const expiresAt = Date.now() + expiryDays * 24 * 60 * 60 * 1000;
    localStorage.setItem(REFERRAL_CODE_KEY, sanitized);
    localStorage.setItem(REFERRAL_CODE_EXPIRES_AT_KEY, expiresAt.toString());
    return true;
  } catch (error) {
    console.error('Failed to store referral code:', error);
    return false;
  }
};

/**
 * Retrieves a valid, non-expired referral code from localStorage
 *
 * @returns The referral code if valid and not expired, null otherwise
 */
export const getReferralCode = (): string | null => {
  try {
    const storedCode = localStorage.getItem(REFERRAL_CODE_KEY);
    const expiresAt = localStorage.getItem(REFERRAL_CODE_EXPIRES_AT_KEY);

    if (!storedCode || !expiresAt) {
      return null;
    }

    const expiresAtTimestamp = parseInt(expiresAt, 10);
    const now = Date.now();

    // Check if referral code has expired
    if (now >= expiresAtTimestamp) {
      // Clean up expired referral code
      clearReferralCode();
      return null;
    }

    // Validate and sanitize the stored code
    const sanitized = sanitizeReferralCode(storedCode);

    if (!sanitized) {
      // Clean up invalid referral code
      clearReferralCode();
      return null;
    }

    return sanitized;
  } catch (error) {
    console.error('Failed to retrieve referral code:', error);
    return null;
  }
};

/**
 * Clears the referral code from localStorage
 */
export const clearReferralCode = (): void => {
  try {
    localStorage.removeItem(REFERRAL_CODE_KEY);
    localStorage.removeItem(REFERRAL_CODE_EXPIRES_AT_KEY);
  } catch (error) {
    console.error('Failed to clear referral code:', error);
  }
};

/**
 * Checks if a referral code exists and is valid
 *
 * @returns true if a valid referral code exists, false otherwise
 */
export const hasReferralCode = (): boolean => {
  return getReferralCode() !== null;
};
